import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
// Removed heroicons import
import { restaurantAPI } from '../services/api';
import toast from 'react-hot-toast';

interface MenuItem {
  id: string;
  name: string;
  description: string;
  price: number;
  category: string;
  available: boolean;
  image?: string;
}

interface CartItem extends MenuItem {
  quantity: number;
}

interface MenuPageProps {
  currentUser?: any;
  onAuthRequired?: () => boolean;
}

const MenuPage = ({ currentUser, onAuthRequired }: MenuPageProps) => {
  const navigate = useNavigate();
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [cart, setCart] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMenuData();
  }, []);

  const loadMenuData = async () => {
    try {
      setLoading(true);
      const [menuResponse, categoriesResponse] = await Promise.all([
        restaurantAPI.getMenu(),
        restaurantAPI.getCategories()
      ]);

      setMenuItems(menuResponse.data || []);
      const categoryNames = categoriesResponse.data?.map((cat: any) => cat.name) || [];
      setCategories(['all', ...categoryNames]);
      
      // Debug logging
      console.log('Menu items loaded:', menuResponse.data?.length || 0);
      console.log('Categories loaded:', categoryNames);
      console.log('Sample menu item:', menuResponse.data?.[0]);
    } catch (error) {
      console.error('Failed to load menu:', error);
      toast.error('Failed to load menu');
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = menuItems.filter(item => {
    // Filter by search term
    const searchMatches = searchTerm === '' || 
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Filter by category
    const categoryMatches = selectedCategory === 'all' || item.category === selectedCategory;
    
    return searchMatches && categoryMatches;
  });

  const addToCart = (item: MenuItem) => {
    setCart(prevCart => {
      const existingItem = prevCart.find(cartItem => cartItem.id === item.id);
      if (existingItem) {
        return prevCart.map(cartItem =>
          cartItem.id === item.id
            ? { ...cartItem, quantity: cartItem.quantity + 1 }
            : cartItem
        );
      }
      return [...prevCart, { ...item, quantity: 1 }];
    });
    toast.success(`${item.name} added to cart`);
  };

  const updateQuantity = (itemId: string, change: number) => {
    setCart(prevCart => {
      return prevCart.map(item => {
        if (item.id === itemId) {
          const newQuantity = Math.max(0, item.quantity + change);
          return newQuantity === 0 
            ? null 
            : { ...item, quantity: newQuantity };
        }
        return item;
      }).filter(Boolean) as CartItem[];
    });
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getCartItemCount = () => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  };

  const getItemQuantityInCart = (itemId: string) => {
    const cartItem = cart.find(item => item.id === itemId);
    return cartItem ? cartItem.quantity : 0;
  };

  const goToCart = () => {
    localStorage.setItem('deliveryCart', JSON.stringify(cart));
    navigate('/cart');
  };

  if (loading) {
    return (
      <div className="container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb' }}>
      {/* Header */}
      <header style={{ 
        background: 'white', 
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)', 
        position: 'sticky', 
        top: 0, 
        zIndex: 10 
      }}>
        <div style={{ 
          padding: '16px', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between' 
        }}>
          <button 
            onClick={() => navigate(-1)} 
            style={{ 
              padding: '8px', 
              border: 'none', 
              background: 'none', 
              cursor: 'pointer',
              fontSize: '18px'
            }}
          >
            ←
          </button>
          <h1 style={{ fontSize: '20px', fontWeight: '600' }}>Menu</h1>
          <button 
            onClick={goToCart}
            style={{ 
              padding: '8px', 
              border: 'none', 
              background: 'none', 
              cursor: 'pointer',
              fontSize: '18px',
              position: 'relative'
            }}
            disabled={cart.length === 0}
          >
            🛒
            {cart.length > 0 && (
              <span style={{
                position: 'absolute',
                top: '-2px',
                right: '-2px',
                background: '#f97316',
                color: 'white',
                fontSize: '12px',
                borderRadius: '50%',
                width: '20px',
                height: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {getCartItemCount()}
              </span>
            )}
          </button>
        </div>
      </header>

      {/* Search Bar */}
      <div style={{ padding: '16px', paddingBottom: '8px' }}>
        <div style={{ position: 'relative' }}>
          <input
            type="text"
            placeholder="Search menu items..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              // Reset category filter when searching to show all matching items
              if (e.target.value && selectedCategory !== 'all') {
                setSelectedCategory('all');
              }
            }}
            style={{
              width: '100%',
              padding: '12px 16px 12px 44px',
              border: '1px solid #d1d5db',
              borderRadius: '24px',
              fontSize: '16px',
              outline: 'none',
              backgroundColor: '#f9fafb',
              transition: 'all 0.2s'
            }}
            onFocus={(e) => {
              e.target.style.backgroundColor = 'white';
              e.target.style.borderColor = '#f97316';
            }}
            onBlur={(e) => {
              e.target.style.backgroundColor = '#f9fafb';
              e.target.style.borderColor = '#d1d5db';
            }}
          />
          <div style={{
            position: 'absolute',
            left: '16px',
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: '16px',
            color: '#9ca3af'
          }}>
            🔍
          </div>
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              style={{
                position: 'absolute',
                right: '16px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                fontSize: '16px',
                color: '#9ca3af',
                cursor: 'pointer',
                padding: '4px'
              }}
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Categories */}
      <div style={{ padding: '16px', paddingTop: '8px' }}>
        <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '8px' }}>
          {categories.map(category => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              style={{
                padding: '8px 16px',
                borderRadius: '20px',
                border: 'none',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                background: selectedCategory === category ? '#f97316' : 'white',
                color: selectedCategory === category ? 'white' : '#374151'
              }}
            >
              {category.charAt(0).toUpperCase() + category.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Menu Items */}
      <div style={{ padding: '0 16px 100px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {filteredItems.map(item => {
            const quantityInCart = getItemQuantityInCart(item.id);
            return (
              <div key={item.id} style={{
                background: 'white',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                padding: '16px',
                display: 'flex',
                justifyContent: 'space-between'
              }}>
                <div style={{ flex: 1, paddingRight: '16px' }}>
                  <h3 style={{ fontWeight: '600', marginBottom: '4px' }}>{item.name}</h3>
                  <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>{item.description}</p>
                  <p style={{ fontSize: '18px', fontWeight: '600', color: '#f97316' }}>₹{item.price}</p>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                  {item.image && (
                    <img
                      src={item.image}
                      alt={item.name}
                      style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px' }}
                    />
                  )}
                  {quantityInCart === 0 ? (
                    <button
                      onClick={() => addToCart(item)}
                      disabled={!item.available}
                      className="btn btn-primary"
                      style={{
                        padding: '8px 16px',
                        fontSize: '14px',
                        opacity: item.available ? 1 : 0.5,
                        cursor: item.available ? 'pointer' : 'not-allowed'
                      }}
                    >
                      {item.available ? 'Add' : 'Unavailable'}
                    </button>
                  ) : (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      background: '#f97316',
                      borderRadius: '8px'
                    }}>
                      <button
                        onClick={() => updateQuantity(item.id, -1)}
                        style={{
                          padding: '8px',
                          background: 'none',
                          border: 'none',
                          color: 'white',
                          cursor: 'pointer',
                          fontSize: '16px'
                        }}
                      >
                        −
                      </button>
                      <span style={{
                        padding: '8px 12px',
                        color: 'white',
                        fontWeight: '600'
                      }}>
                        {quantityInCart}
                      </span>
                      <button
                        onClick={() => updateQuantity(item.id, 1)}
                        style={{
                          padding: '8px',
                          background: 'none',
                          border: 'none',
                          color: 'white',
                          cursor: 'pointer',
                          fontSize: '16px'
                        }}
                      >
                        +
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Cart Summary Footer */}
      {cart.length > 0 && (
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: '100%',
          maxWidth: '448px',
          background: 'white',
          borderTop: '1px solid #e5e7eb',
          padding: '16px'
        }}>
          <button
            onClick={goToCart}
            className="btn btn-primary"
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '12px 16px'
            }}
          >
            <span>View Cart ({getCartItemCount()} items)</span>
            <span>₹{getCartTotal().toFixed(2)}</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default MenuPage;